import {
  BOOK_DOWNLOADING_STATUS,
  CONTENT_SYNCHRONIZATION_STATUS,
  DOWNLOAD,
  FILE_TYPES,
  LOCAL_UNIT_REPOSITORY
} from '../const';
import { requireFromElectron } from '../services/crossPlatform/crossPlatformService';
import * as bookApi from '../services/api/bookApi';
import * as _ from 'lodash';
import { notifyAuthory } from '../electron/windowManager';
import { getLogger } from '../services/logger';
import { sendLTIOffline } from '../actions/ltiActions';
import { saveScaResponseToFile, updateScaWithLocalResources } from '../services/file/fileSystemService';

const jsSHA = requireFromElectron('jssha');
const debug = getLogger('utils:contentUtils');

export function getBookActivitiesContent(book, activitiesContent) {
  return _.filter(activitiesContent, activityContent => {
    return book.id === activityContent.bookId && book.classId === activityContent.classId;
  });
}

export function isStoreContentBookDownloaded(book, contentBook) {
  return (
    book &&
    book.id &&
    contentBook &&
    contentBook[book.id] &&
    contentBook[book.id].status === BOOK_DOWNLOADING_STATUS.COMPLETED
  );
}

export function isStoreContentBookSynchronizing(book, contentBooks) {
  return book && book.id && contentBooks && contentBooks[book.id] && contentBooks[book.id].isSynchronizing;
}

export function isStoreContentBookDownloading(book, contentBook) {
  return (
    book &&
    book.id &&
    contentBook &&
    contentBook[book.id] &&
    (contentBook[book.id].status === BOOK_DOWNLOADING_STATUS.DOWNLOADING ||
      contentBook[book.id].status === BOOK_DOWNLOADING_STATUS.PAUSE)
  );
}

export function isStoreContentBookSaving(book, contentBook) {
  return (
    book &&
    book.id &&
    contentBook &&
    contentBook[book.id] &&
    (contentBook[book.id].status === BOOK_DOWNLOADING_STATUS.DOWNLOADING ||
      contentBook[book.id].status === BOOK_DOWNLOADING_STATUS.PAUSE ||
      contentBook[book.id].status === BOOK_DOWNLOADING_STATUS.UNZIPPING)
  );
}

export function isStoreContentBookPaused(book, contentBook) {
  return (
    book &&
    book.id &&
    contentBook &&
    contentBook[book.id] &&
    contentBook[book.id].status === BOOK_DOWNLOADING_STATUS.PAUSE
  );
}

export function getActivityContentId(bookContext, activity) {
  return `${bookContext.bookId}_${activity.id}`;
}

export function getScaResponseSha(scaResponse) {
  let shaObj = new jsSHA('SHA-256', 'TEXT');
  shaObj.update(JSON.stringify(scaResponse));
  return shaObj.getHash('HEX');
}

export function getFilteredCmi5ResponseByUnitId(classId, bookId, bookActivityId, unitId = null, withResources = true) {
  return bookApi.getCmi5(classId, bookId, bookActivityId, withResources).then(bookCmi5Response => {
    if (unitId && !_.isNull(unitId)) {
      let bookCmi5 = bookCmi5Response.data;
      let bookCmi5Units = bookCmi5.auOrBlock;
      bookCmi5Units = unitId ? _.filter(bookCmi5Units, u => u.id === unitId) : bookCmi5Units;
      bookCmi5Response.data.auOrBlock = bookCmi5Units;
    }
    return bookCmi5Response.data;
  });
}

export function getClassroomComponentsByUnitId(classId, unitId) {
  return bookApi.getClassroomComponents(classId, unitId).then(response => {
    return response.data;
  });
}

export function getResourcesByUnitId(customerId, activityIds) {
  return bookApi.getResources(customerId, activityIds).then(response => {
    return response.data;
  });
}

export function getUnitNotes(classId, unitActivityId, userId) {
  return bookApi.getUnitNotes(classId, unitActivityId, userId).then(notesResponse => {
    return notesResponse;
  });
}

export function areAllBookContentsUnzipping(bookActivitiesContent) {
  let allActivitiesContentsAreUnzipping = true;
  bookActivitiesContent.forEach(activityContent => {
    if (
      activityContent.format === FILE_TYPES.ZIP.mimeType &&
      activityContent.downloadStatus !== BOOK_DOWNLOADING_STATUS.UNZIPPING &&
      activityContent.downloadStatus !== BOOK_DOWNLOADING_STATUS.COMPLETED
    ) {
      allActivitiesContentsAreUnzipping = false;
    }
  });

  return allActivitiesContentsAreUnzipping;
}

export function getContentBookByBookIdAndClassId(contentBooks, bookId, classId, id = null) {
  const contentBook = contentBooks[bookId];
  if (contentBook) {
    if (!_.isNil(id)) {
      if (contentBook.classId === classId && contentBook.id === id) {
        return contentBook;
      }
    } else if (bookId) {
      if (contentBook.classId === classId) {
        return contentBook;
      }
    }
  }
}

export function isContentBookDownloadingOrUnzipping(contentBook) {
  return (
    contentBook &&
    (contentBook.status === BOOK_DOWNLOADING_STATUS.DOWNLOADING ||
      contentBook.status === BOOK_DOWNLOADING_STATUS.UNZIPPING)
  );
}

export function isContentBooksDownloadingOrUnzipping(contentBooks) {
  const contentBooksArray = [];

  for (let key in contentBooks) {
    contentBooksArray.push(contentBooks[key]);
  }

  return contentBooksArray.filter(isContentBookDownloadingOrUnzipping);
}

export const createBookContext = (userId, classId, bookId, bookActivityId, bookName, customerId) => {
  return { userId, classId, bookId, bookActivityId, bookName, customerId };
};

export const createUnitContext = (userId, classId, bookId, bookActivityId, unitActivityId, bookName) => {
  return { userId, classId, bookId, bookActivityId, unitActivityId, bookName };
};

export function notifyAuthoringToContentWindow(unitActivityId, bookContext) {
  notifyAuthory(bookContext.bookActivityId, { unitUri: unitActivityId });
}

export function isStoredBookDownloading(contentBooks, bookContext) {
  const storeContentBook = getContentBookByBookIdAndClassId(contentBooks, bookContext.bookId, bookContext.classId);
  return (
    storeContentBook &&
    (storeContentBook.status === BOOK_DOWNLOADING_STATUS.DOWNLOADING ||
      storeContentBook.status === BOOK_DOWNLOADING_STATUS.UNZIPPING)
  );
}

export function postLtiParameters(launchUrl, account, institution, classroom) {
  try {
    const ltiBookConfig = {
      launchUrl,
      account,
      institution,
      classroom
    };

    return sendLTIOffline(ltiBookConfig);
  } catch (error) {
    debug.error('[postLtiParameters]', error);
  }
}

export async function saveSmartUnitScaResponse(context, scaResponse, customerId) {
  if (scaResponse && !_.isNil(scaResponse)) {
    scaResponse = await updateScaWithLocalResources(scaResponse, context, customerId);
    await saveScaResponseToFile(context, scaResponse);
  }
}

export function getLocalRepoUnitPath(routerLocation, unitOrigFileName) {
  return (routerLocation.query.localUnitRepo || `${LOCAL_UNIT_REPOSITORY}`) + `/${unitOrigFileName}`;
}

export function newContentBook(book, classId, bookName) {
  const contentBook = createContentBookStructure();
  contentBook.id = book.id;
  contentBook.isCustomBook = isCustomContent(book);
  contentBook.bookId = book.activityId;
  contentBook.classId = classId;
  contentBook.bookName = bookName;

  return contentBook;
}

export function createContentBookStructure(values = {}) {
  return {
    id: values.id ? values.id : '',
    isCustomBook: values.isCustomBook ? values.isCustomBook : false,
    bookId: values.bookId ? values.bookId : '',
    classId: values.classId ? values.classId : '',
    bookName: values.bookName ? values.bookName : '',
    cmi5etag: values.cmi5etag ? values.cmi5etag : [],
    fromCatalog: values.fromCatalog ? values.fromCatalog : '',
    documentLessons: [],
    status: values.status ? values.status : BOOK_DOWNLOADING_STATUS.EMPTY,
    isSynchronizing: false
  };
}

export function createActivityContentStructure(bookContext, activity) {
  return {
    activityContentId: getActivityContentId(bookContext, activity),
    bookId: bookContext.bookId,
    classId: bookContext.classId,
    format: activity.format,
    mode: activity.mode,
    downloadPercentage: DOWNLOAD.EMPTY,
    totalSizeToDownload: DOWNLOAD.EMPTY,
    unzipPercentage: DOWNLOAD.EMPTY,
    downloadStatus: BOOK_DOWNLOADING_STATUS.EMPTY
  };
}

export function checkRequestPaused(contentBooks, bookId, classId) {
  const contentBook = getContentBookByBookIdAndClassId(contentBooks, bookId, classId);
  const bookIsPaused = contentBook.status === BOOK_DOWNLOADING_STATUS.PAUSE;
  const bookIsCanceled = contentBook.status === BOOK_DOWNLOADING_STATUS.CANCEL;
  return bookIsPaused || bookIsCanceled;
}

export function resetDownloadProps(element) {
  element.totalSizeToDownload = 0;
  element.downloadPercentage = DOWNLOAD.EMPTY;
  element.unzipPercentage = DOWNLOAD.EMPTY;
  if (element.downloadStatus) {
    element.downloadStatus = BOOK_DOWNLOADING_STATUS.EMPTY;
  }
}

export function isContentAlreadyDownloading(content) {
  return content.contentSynchronizationStatus === CONTENT_SYNCHRONIZATION_STATUS.SYNCHRONIZING;
}

/**
 * Check if a classroom content is downloaded
 * @param classId
 * @param contentBooks
 * @returns {boolean}
 */
export let isClassroomContentDownloaded = (classId, contentBooks) => {
  let isDownloaded = true;
  const classroomContentBooks = getClassroomContentBooks(classId, contentBooks);

  if (!classroomContentBooks.length) {
    isDownloaded = true;
  }

  classroomContentBooks.forEach(contentBook => {
    if (contentBook.status !== BOOK_DOWNLOADING_STATUS.COMPLETED) {
      isDownloaded = false;
    }
  });

  return isDownloaded;
};

export function getBooksSaving(classId, contentBooks) {
  return _.filter(contentBooks, download => {
    return (
      download.classId === classId &&
      (download.status === BOOK_DOWNLOADING_STATUS.DOWNLOADING ||
        download.status === BOOK_DOWNLOADING_STATUS.PAUSE ||
        download.status === BOOK_DOWNLOADING_STATUS.UNZIPPING)
    );
  });
}

export function getBooksDownloading(classId, book) {
  return (
    book.classId === classId &&
    (book.status === BOOK_DOWNLOADING_STATUS.DOWNLOADING || book.status === BOOK_DOWNLOADING_STATUS.PAUSE)
  );
}

export function getBooksUnzipping(classId, book) {
  return book.classId === classId && book.status === BOOK_DOWNLOADING_STATUS.UNZIPPING;
}

export function getBookSynchronizing(bookId, contentBook) {
  if (contentBook && bookId) {
    return contentBook.id === bookId && contentBook.status && contentBook.isSynchronizing;
  }
  return false;
}

export function getClassroomBooksDownloading(classId, contentBooks) {
  if (classId) {
    return _.filter(contentBooks, download => {
      return download.classId === classId && download.status === BOOK_DOWNLOADING_STATUS.DOWNLOADING;
    });
  }
}

export function getAllBooksDownloading(contentBooks) {
  return _.filter(contentBooks, download => {
    return download.status === BOOK_DOWNLOADING_STATUS.DOWNLOADING;
  });
}

export function isBookSaving(classId, contentBook) {
  if (contentBook) {
    const isDownloading = contentBook.status === BOOK_DOWNLOADING_STATUS.DOWNLOADING;
    const isPaused = contentBook.status === BOOK_DOWNLOADING_STATUS.PAUSE;
    const isUnizipping = contentBook.status === BOOK_DOWNLOADING_STATUS.UNZIPPING;
    return contentBook.classId === classId && (isDownloading || isPaused || isUnizipping);
  }
  return false;
}

export function getClassroomBooksSynchronizing(contentBooks, classId) {
  let result = false;
  Object.values(contentBooks).map(book => {
    if (book && classId) {
      if (book.classId === classId && book.status && book.isSynchronizing) {
        result = true;
        return;
      }
    }
  });
  return result;
}

export function getBooksDownloadingByBookIdAndClassId(contentBook, classId) {
  if (contentBook && contentBook.status === BOOK_DOWNLOADING_STATUS.DOWNLOADING && contentBook.classId === classId) {
    return contentBook;
  }
  return {};
}

export function isCustomContent(activity) {
  return activity.course ? !activity.course.fromCatalog : !activity.fromCatalog;
}

export function isEmptyContent(content) {
  let isEmptyContent = true;

  if (!_.isEmpty(content)) {
    content.auOrBlock.forEach(unit => {
      if (!_.isEmpty(unit.auOrBlock)) {
        isEmptyContent = false;
      }
    });
  }

  return isEmptyContent;
}

export function getContentBook(getState, book, classroomId, bookName) {
  const savedContentBook = getState().content.contentBooks[book.id];
  return savedContentBook ? savedContentBook : newContentBook(book, classroomId, bookName);
}

export function checkContentAuthoringResources(unitLocalCmi5, unit) {
  const hasAuthoringResourcesUpdates = [];
  const removedAuthoringResources = [];
  if (unit.resources && unitLocalCmi5.resources) {
    for (let key in unit.resources) {
      const resource = unit.resources[key];
      const localResource = unitLocalCmi5.resources[key];
      if (!localResource || resource.url !== localResource.resourceUrl) {
        hasAuthoringResourcesUpdates.push(resource);
      }
      delete unitLocalCmi5.resources[key];
    }
    for (const key in unitLocalCmi5.resources) {
      if (unitLocalCmi5.resources.hasOwnProperty(key)) {
        removedAuthoringResources.push(unitLocalCmi5.resources[key]);
      }
    }
  } else if (unit.resources) {
    for (let key in unit.resources) {
      hasAuthoringResourcesUpdates.push(unit.resources[key]);
    }
  }
  return { hasAuthoringResourcesUpdates, removedAuthoringResources };
}

export function updateAuthoringObject(unit) {
  if (unit.authoring && unit.resources && !_.isEmpty(unit.resources)) {
    const authoring = JSON.parse(unit.authoring);
    const resourcesKeys = Object.keys(unit.resources);

    resourcesKeys.forEach(key => {
      if (authoring[key]) {
        authoring[key].extendedProperties.srcName.url = unit.resources[key].url;
      }
    });
    unit.authoring = JSON.stringify(authoring);
  }
}

function getClassroomContentBooks(classId, contentBooks) {
  return _.filter(contentBooks, contentBook => {
    return contentBook.classId === classId;
  });
}
